/** Shared comment HTML + image preview helpers for review panel and activity popup. */

const IMAGE_URL_IN_TEXT =
  /(https?:\/\/[^\s<>"]+?\.(?:jpg|jpeg|png|gif|webp|svg|bmp)(?:\?[^\s<>"]*)?)/gi

export const COMMENT_IMAGE_ROOT_SELECTOR =
  ".comment-message, .pc-text, .pc-body, .comments-popup-body, .comment-image-wrap, .comment-section"

export function commentTextIncludesImageMarkup(text) {
  if (!text) return false
  if (/<img[\s>]/i.test(text)) return true
  return /https?:\/\/[^\s<>"]+?\.(?:jpg|jpeg|png|gif|webp|svg|bmp)/i.test(text)
}

export function getCommentAttachmentUrl(comment) {
  if (!comment) return null
  const direct =
    comment.mediaLink ||
    comment.comment?.mediaLink ||
    comment.fileUrl ||
    comment.imageUrl ||
    comment.attachmentUrl ||
    comment.comment?.fileUrl ||
    comment.mediaUrl ||
    comment.comment?.mediaUrl ||
    comment.contentTopicPostComment?.fileUrl ||
    comment.contentTopicPostComment?.mediaUrl ||
    null
  if (direct) return direct
  const text = comment.commentText || comment.comment?.commentText || ""
  if (!text) return null
  const parsed = parseCommentBodyForEdit(text)
  return parsed.imageUrls?.[0] || null
}

/** Insert media URL into edit-request commentText when To is empty. */
export function injectEditRequestToMediaUrl(commentText, toUrl) {
  if (!commentText || !toUrl || !commentText.includes("edit-request-reason")) {
    return commentText
  }
  if (/To\s*<\/strong>\s*https?:\/\//i.test(commentText)) {
    return commentText
  }
  return commentText.replace(
    /(To\s*<\/strong>\s*)(<strong><br\s*\/?>\s*Remarks)/i,
    `$1 ${toUrl}$2`,
  )
}

function addPreviewClassesToImgTags(html) {
  return html.replace(/<img\b([^>]*)>/gi, (full, attrs) => {
    const classMatch = attrs.match(/class\s*=\s*["']([^"']*)["']/i)
    if (classMatch) {
      const classes = new Set(classMatch[1].split(/\s+/).filter(Boolean))
      classes.add("comment-inline-image")
      classes.add("review-previewable-image")
      const newAttrs = attrs.replace(classMatch[0], `class="${[...classes].join(" ")}"`)
      return `<img${newAttrs}>`
    }
    return `<img class="comment-inline-image review-previewable-image"${attrs}>`
  })
}

/**
 * Turn comment text into HTML safe for display, with clickable previewable images.
 */
export function enhanceCommentMessageHtml(html, attachmentUrl = null) {
  if (!html) return ""

  let result = html

  if (html.includes("edit-request-reason")) {
    const toMatch = html.match(
      /To\s*<\/strong>\s*(https?:\/\/[^\s<>"]+?\.(?:jpg|jpeg|png|gif|webp|svg|bmp)(?:\?[^\s<>"]*)?)/i,
    )
    const fromMatch = html.match(
      /From\s*-\s*<\/strong>\s*(https?:\/\/[^\s<>"]+?\.(?:jpg|jpeg|png|gif|webp|svg|bmp)(?:\?[^\s<>"]*)?)/i,
    )
    const remarksMatch = html.match(/Remarks\s*<\/strong>\s*(.*)/is)
    const imageUrl = toMatch?.[1] || attachmentUrl || fromMatch?.[1] || null
    result = ""
    if (imageUrl) {
      result += `<img src="${imageUrl}" alt="media" class="comment-inline-image review-previewable-image" />`
    }
    if (remarksMatch?.[1]) {
      const text = remarksMatch[1].replace(/<[^>]*>/g, "").trim().replace(/,+$/, "").trim()
      if (text) result += `<div style="margin-top:6px">${text}</div>`
    }
  } else if (!/<img[\s>]/i.test(html)) {
    result = html.replace(
      IMAGE_URL_IN_TEXT,
      '<br/><img src="$1" alt="media" class="comment-inline-image review-previewable-image" />',
    )
  }

  return addPreviewClassesToImgTags(result)
}

export function isCommentPreviewImage(img) {
  if (!img || !(img instanceof HTMLImageElement)) return false
  return Boolean(img.closest(COMMENT_IMAGE_ROOT_SELECTOR))
}

const MEDIA_URL_CAPTURE =
  /(https?:\/\/[^\s<>"']+?\.(?:jpg|jpeg|png|gif|webp|svg|bmp)(?:\?[^\s<>"']*)?)/gi

/** Plain remarks + image URLs for edit UI (mirrors view-mode extraction). */
export function parseCommentBodyForEdit(rawText) {
  const text = (rawText || "").trim()
  if (!text) {
    return { kind: "plain", remarks: "", imageUrls: [], fromUrl: null, toUrl: null }
  }

  if (text.includes("edit-request-reason")) {
    const fromMatch = text.match(/From\s*-\s*<\/strong>\s*(https?:\/\/[^\s<>"']+)/i)
    const toMatch = text.match(/To\s*<\/strong>\s*(https?:\/\/[^\s<>"']+)/i)
    const remarksMatch = text.match(/Remarks\s*<\/strong>\s*(.*)/is)
    let remarks = remarksMatch?.[1] || ""
    remarks = remarks.replace(/<[^>]*>/g, "").replace(/,+\s*$/, "").trim()
    const toUrl = toMatch?.[1]?.trim() || null
    const fromUrl = fromMatch?.[1]?.trim() || null
    const displayUrl = toUrl || fromUrl || null
    return {
      kind: "edit-request-reason",
      remarks,
      imageUrls: displayUrl ? [displayUrl] : [],
      fromUrl,
      toUrl: toUrl || displayUrl,
    }
  }

  const imageUrls = []
  let match
  const regex = new RegExp(MEDIA_URL_CAPTURE.source, "gi")
  while ((match = regex.exec(text)) !== null) {
    imageUrls.push(match[1])
  }
  const uniqueUrls = [...new Set(imageUrls)]
  let remarks = text
  uniqueUrls.forEach((url) => {
    remarks = remarks.split(url).join(" ")
  })
  remarks = remarks.replace(/\s+/g, " ").trim()
  return { kind: "plain", remarks, imageUrls: uniqueUrls, fromUrl: null, toUrl: null }
}

export function serializeCommentBodyForSave(draft) {
  if (!draft) return ""
  const remarks = (draft.remarks ?? "").trim()
  const toUrl = draft.imageUrls?.[0] || draft.toUrl || ""
  const fromUrl = draft.fromUrl || ""

  if (draft.kind === "edit-request-reason") {
    if (!remarks && !toUrl && !fromUrl && !draft.newFile) return ""
    const suffix = remarks ? ", " : ""
    return `edit-request-reason-<strong>Change media:<br /> From -</strong> ${fromUrl}<strong><br />To </strong> ${toUrl}<strong><br />Remarks </strong> ${remarks}${suffix}`
  }

  return remarks
}

/** PATCH /comments/{id}: file = replace; media_link + empty file = keep URL; empty file only = remove attachment. */
export function buildCommentUpdateRequest(draft) {
  const comment = serializeCommentBodyForSave(draft)
  const file = draft?.newFile || null
  if (file) {
    return { comment, file, mediaLink: null, removeMedia: false }
  }

  const keptUrl =
    draft?.kind === "edit-request-reason"
      ? draft?.imageUrls?.[0] || draft?.toUrl || null
      : draft?.imageUrls?.[0] || null

  if (keptUrl) {
    return { comment, file: null, mediaLink: keptUrl, removeMedia: false }
  }

  if (draft?.hadAttachmentInitially) {
    return { comment, file: null, mediaLink: null, removeMedia: true }
  }

  return { comment, file: null, mediaLink: null, removeMedia: false }
}

export function commentEditDraftHasContent(draft) {
  if (!draft) return false
  return (
    Boolean((draft.remarks ?? "").trim()) ||
    (draft.imageUrls?.length ?? 0) > 0 ||
    Boolean(draft.newFile)
  )
}

export function buildCommentEditDraft(rawText, attachmentUrl) {
  const parsed = parseCommentBodyForEdit(rawText)
  const imageUrls = [...(parsed.imageUrls || [])]
  if (attachmentUrl && !imageUrls.includes(attachmentUrl)) {
    imageUrls.push(attachmentUrl)
  }
  const imageUrlsForEdit = imageUrls.slice(0, 1)
  return {
    ...parsed,
    imageUrls: imageUrlsForEdit,
    newFile: null,
    newFilePreview: null,
    hadAttachmentInitially: imageUrlsForEdit.length > 0,
  }
}
